import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteIcon from '@mui/icons-material/Delete';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { Button, Checkbox, Grid, IconButton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import { FormikTextField } from '../../../common/formik-material-ui/FormikTextField';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { AddAgentsUploadModal } from './AddAgentsUploadModal';
import OnboardingSkeleton from './OnboardingSkeleton';

import { useFeatureFlag, useDynamicConfigObject } from 'src/app/hooks/useFeatureFlag';

const useStyles = makeStyles((theme: Theme) => ({
  subtitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: 'grey',
  },
  uploadContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  uploadDescription: {
    fontWeight: 400,
    marginRight: theme.spacing(1),
  },
  uploadButton: {
    fontSize: 12,
    fontWeight: 600,
  },
  agentsContainer: {
    marginBottom: theme.spacing(4),
  },
  addAgentsDescription: {
    fontWeight: 400,
    marginBottom: '2.5rem',
  },
  agentsList: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1),
  },
  agentItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
    margin: theme.spacing(1),
    marginBottom: 0,
    '& > div': {
      marginRight: theme.spacing(1),
    },
  },
  field: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  teamSelect: {
    marginTop: '0px',
    minWidth: 125,
  },
  addAgentButton: {
    fontSize: 12,
    fontWeight: 600,
    marginLeft: 10,
    marginBottom: '2rem',
  },
}));

const NO_TEAM_ID = 'No Team';

export interface IAgent {
  firstName: string;
  lastName: string;
  email: string;
  teams: string[];
}

const hasAnyDuplicateEmails = (agents: IAgent[]): boolean => {
  const agentsByEmail = {};
  for (const agent of agents) {
    const email = agent.email.toLowerCase().trim();
    agentsByEmail[email] = (agentsByEmail[email] ?? 0) + 1;
  }
  return (
    Object.values(agentsByEmail).filter((count) => Number(count) > 1).length > 0
  );
};

const hasBeenModified = (agent: IAgent): boolean =>
  agent.firstName.length > 0 ||
  agent.lastName.length > 0 ||
  agent.email.length > 0 ||
  agent.teams.length > 0;

export function AddAgents({
  loading,
  onboardingClasses,
  handleStep,
  handleBack,
  mutation,
  userAtom,
  activeStep,
  owWizardDataParsed,
}) {
  const classes = useStyles();
  const { treatment, isReady } = useFeatureFlag('add-agent');

  const addAgentConfig = useDynamicConfigObject('add-agent', {});

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const [addAgentModal, showAddAgentModal] = useState<boolean>(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        agents: Yup.array()
          .of(
            Yup.object().shape({
              firstName: Yup.string().required('required'),
              lastName: Yup.string().required('required'),
              email: Yup.string()
                .email('Must be a valid Email Address')
                .required('required'),
              teams: Yup.array()
                .of(Yup.string().required('required'))
                .min(1, 'required'),
            })
          )
          .min(1, 'required'),
      }),
    []
  );

  const [agents, setAgents] = useState<any>([]);

  useEffect(() => {
    setAgents(
      owWizardDataParsed?.agents?.length
        ? owWizardDataParsed?.agents
        : [
            {
              firstName: '',
              lastName: '',
              email: '',
              teams: [],
            },
          ]
    );
  }, []);

  const getLastIndexWithAnyData = (formik) => {
    let lastIndex = formik?.values?.agents?.length - 1;
    for (; lastIndex >= 0; lastIndex--) {
      const agent = formik?.values?.agents?.[lastIndex];
      if (hasBeenModified(agent)) {
        return lastIndex + 1;
      }
    }
    return 0;
  };

  const addAgents = (newAgents, formik) => {
    newAgents = newAgents.map((agent) => {
      const existingTeamNames: { [team: string]: boolean } = (
        owWizardDataParsed?.teams ?? []
      )
        .map((team) => team.name.toLowerCase().trim())
        .reduce((acc, team) => {
          acc[team] = true;
          return acc;
        }, {});
      return {
        ...agent,
        teams: (agent.teams ?? []).filter(
          (team) =>
            existingTeamNames[team.toLowerCase().trim()] || team === NO_TEAM_ID
        ),
      };
    });

    const lastIndex = getLastIndexWithAnyData(formik);

    for (let i = lastIndex; i < lastIndex + newAgents.length; i++) {
      formik.setFieldTouched(`agents.${i}.firstName`, true);
      formik.setFieldTouched(`agents.${i}.lastName`, true);
      formik.setFieldTouched(`agents.${i}.email`, true);
      formik.setFieldTouched(`agents.${i}.teams`, true);
    }

    const agents = [...formik.values.agents];
    const emails = agents.map((agent) => agent.email.toLowerCase().trim());
    const updatedAgents = [
      ...agents.slice(0, lastIndex),
      ...newAgents.filter(
        (agent) =>
          agent.email && !emails.includes(agent.email.toLowerCase().trim())
      ),
    ];
    setAgents(updatedAgents);

    formik.setFieldValue('agents', updatedAgents, true);

    formik.validateForm();
  };

  const tryContinue = async (formik) => {
    if (hasAnyDuplicateEmails(formik?.values?.agents)) {
      enqueueSnackbarWithStyles({
        message:
          'Found agents with the same email. Please fix before continuing.',
        alertProps: { severity: 'error' },
      });
      return;
    }

    await formik.validateForm();
    if (formik.isValid) {
      formik.submitForm();
    }
  };

  const teams = React.useMemo(
    () =>
      owWizardDataParsed?.teams?.length
        ? owWizardDataParsed?.teams?.map((t) => t?.name).concat([NO_TEAM_ID])
        : [NO_TEAM_ID],
    [owWizardDataParsed]
  );

  return (
    <>
      {loading ? (
        <OnboardingSkeleton />
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{ agents: agents }}
          validationSchema={validationSchema}
          onSubmit={async (values, _actions) => {
            const updateOnboardingWizard = () =>
              mutation.mutateAsync({
                endpoint: endpointEnum.UPDATE_OW_WIZARD,
                domain: process.env.VITE_APP_CDK_DOMAIN,
                method: 'put',
                stage: 'itn',
                payload: {
                  apiKey: userAtom?.apikey,
                  businessId: userAtom?.businessid,
                  owWizardData: JSON.stringify({
                    ...owWizardDataParsed,
                    activeStep: 4,
                    agents: values?.agents,
                  }),
                },
              });

            try {
              const [onboardingWizard] = await Promise.allSettled<any>([
                updateOnboardingWizard(),
              ]);

              const isSuccessful = (result) =>
                result.status === 'fulfilled' ||
                (result.status === 'rejected' &&
                  result.reason.response?.status === 304);

              if (isSuccessful(onboardingWizard)) {
                enqueueSnackbarWithStyles({
                  message: 'Successfully Updated Onboarding Wizard',
                  alertProps: { severity: 'success' },
                });
                handleStep(4);
              } else {
                enqueueSnackbarWithStyles({
                  message: 'Encountered an error updating Onboarding Wizard',
                  alertProps: { severity: 'error' },
                });
              }
            } catch (err) {
              enqueueSnackbarWithStyles({
                message: 'Encountered an error updating Onboarding Wizard',
                alertProps: { severity: 'error' },
              });
            }
          }}
        >
          {(formik) => (
            <>
              <AddAgentsUploadModal
                show={!!addAgentModal}
                onHide={() => showAddAgentModal(false)}
                addAgents={addAgents}
                formik={formik}
              />
              <div style={{ padding: '24px' }}>
                <header>
                  <h4 className={onboardingClasses.onboardingSubtitle}>
                    Building Your Team - Adding Agents
                  </h4>
                </header>
                <div className={classes.uploadContainer}>
                  <span className={classes.uploadDescription}>
                    Click to upload a spreadsheet with existing agents
                  </span>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => showAddAgentModal(true)}
                    startIcon={<GridOnOutlinedIcon />}
                    className={classes.uploadButton}
                  >
                    Upload
                  </Button>
                </div>
                <p className={classes.addAgentsDescription}>
                  To manually add agents, add your first agents and their
                  respective team (more can be added later).
                </p>
                <div className={classes.agentsContainer}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={9} md={9} lg={9} xl={9}>
                      <div className={classes.agentsList}>
                        {formik?.values?.agents?.map((agent, index) => (
                          <div
                            key={`agent-${index}`}
                            className={classes.agentItem}
                          >
                            <div className={classes.field}>
                              <FormikTextField
                                label="First Name"
                                variant="outlined"
                                formik={formik}
                                name={`agents.${index}.firstName`}
                                size="small"
                                required
                                value={agent?.firstName ?? ''}
                              />
                            </div>
                            <div className={classes.field}>
                              <FormikTextField
                                label="Last Name"
                                variant="outlined"
                                formik={formik}
                                name={`agents.${index}.lastName`}
                                size="small"
                                required
                                value={agent?.lastName ?? ''}
                              />
                            </div>
                            <div className={classes.field}>
                              <FormikTextField
                                label="Email"
                                variant="outlined"
                                formik={formik}
                                name={`agents.${index}.email`}
                                size="small"
                                required
                                value={agent?.email ?? ''}
                              />
                            </div>
                            <div style={{ width: '275px' }}>
                              <MaterialAutocomplete
                                type="text"
                                options={teams}
                                multiple={true}
                                label="Select Teams"
                                size="small"
                                variant="outlined"
                                required
                                value={
                                  agent?.teams?.length > 0 ? agent.teams : []
                                }
                                getOptionLabel={(option: any) => option}
                                onChange={(
                                  e,
                                  selectedOptions,
                                  reason,
                                  details
                                ) => {
                                  if (reason === 'selectOption') {
                                    if (
                                      details?.option !== NO_TEAM_ID &&
                                      selectedOptions?.length > 1 &&
                                      selectedOptions.includes(NO_TEAM_ID)
                                    ) {
                                      setTimeout(() => {
                                        const updatedItems = selectedOptions.filter(
                                          (item) => item !== NO_TEAM_ID
                                        );
                                        formik.setFieldValue(
                                          `agents.${index}.teams`,
                                          updatedItems
                                        );
                                      });
                                    }
                                    formik.setFieldValue(
                                      `agents.${index}.teams`,
                                      details?.option === NO_TEAM_ID
                                        ? [NO_TEAM_ID]
                                        : selectedOptions
                                    );
                                  } else if (reason === 'removeOption') {
                                    formik.setFieldValue(
                                      `agents.${index}.teams`,
                                      selectedOptions
                                    );
                                  } else if (reason === 'clear') {
                                    formik.setFieldValue(
                                      `agents.${index}.teams`,
                                      []
                                    );
                                  }
                                }}
                                renderOption={(props, option, { selected }) => {
                                  return (
                                    <li {...props}>
                                      <>
                                        <Checkbox
                                          color="primary"
                                          icon={
                                            <CheckBoxOutlineBlankIcon fontSize="small" />
                                          }
                                          checkedIcon={
                                            <CheckBoxIcon fontSize="small" />
                                          }
                                          style={{ marginRight: 8 }}
                                          checked={selected}
                                        />
                                        {option}
                                      </>
                                    </li>
                                  );
                                }}
                              />
                            </div>
                            <div className={classes.field}>
                              <IconButton
                                color="default"
                                aria-label="Delete"
                                component="label"
                                style={{ marginTop: -4.5 }}
                                disabled={formik.values.agents.length === 1}
                                onClick={() => {
                                  const agents = [
                                    ...formik.values.agents.slice(0, index),
                                    ...formik.values.agents.slice(index + 1),
                                  ];
                                  formik.setFieldValue('agents', agents, true);
                                }}
                                size="large"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={3} md={3} lg={3} xl={3}>
                      <Grid container spacing={1}>
                        {teams?.length &&
                          teams?.map((t) => (
                            <Grid
                              xs={6}
                              sm={6}
                              md={6}
                              lg={6}
                              xl={6}
                              item
                              style={{ color: 'rgba(0, 0, 0, 0.54)' }}
                              key={`team-${t}`}
                            >
                              {t}
                              <div>
                                {formik?.values?.agents?.length &&
                                  formik?.values?.agents?.map((agent, k) => (
                                    <div
                                      key={`team-${t}-agent-${k}`}
                                      style={{ fontWeight: 'bold' }}
                                    >
                                      {agent?.teams.includes(t) && (
                                        <span>
                                          {agent?.firstName} {agent?.lastName}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </Grid>
                          ))}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Button
                    variant="contained"
                    startIcon={<AddOutlinedIcon />}
                    className={classes.addAgentButton}
                    onClick={() =>
                      formik.setFieldValue(
                        'agents',
                        [
                          ...formik.values.agents,
                          {
                            firstName: '',
                            lastName: '',
                            email: '',
                            teams: [],
                          } as any,
                        ],
                        true
                      )
                    }
                  >
                    {isReady ?
                      (treatment === 'add-teammate' ? 'Add Teammate' : 'Add Agent')
                      : 'Add Agent'
                    }
                  </Button>
                  <div style={{ padding: '24px 0px 0px' }}>
                    <Button
                      onClick={() => handleBack(activeStep - 1)}
                      className={onboardingClasses.button}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => tryContinue(formik)}
                      className={onboardingClasses.button}
                      disabled={
                        !formik.isValid || formik.values.agents.length === 0
                      }
                      endIcon={<ArrowForwardIcon />}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </Formik>
      )}
    </>
  );
}
